import { Box, CircularProgress, Typography } from "@mui/material";
import { Eye, Save, X } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "./Button";
import { usePreviewMode } from "../contexts/PreviewModeContext";
import { useSettings } from "../contexts/SettingsContext";

const PreviewBanner = () => {
  const { t } = useTranslation("settings");
  const {
    isPreviewMode,
    previewTemplate,
    isDirty,
    saving,
    saveAndPush,
    saveDraft,
    discard,
  } = usePreviewMode();
  const { setPreviewOverrides, clearPreviewOverrides } = useSettings();

  // Sync preview template configs into SettingsContext overrides
  useEffect(() => {
    if (isPreviewMode && previewTemplate) {
      setPreviewOverrides({
        dashboard_config: previewTemplate.dashboard_config,
        analytics_config: previewTemplate.analytics_config,
        asset_analytics_config: previewTemplate.asset_analytics_config,
      });
    } else {
      clearPreviewOverrides();
    }
  }, [
    isPreviewMode,
    previewTemplate,
    setPreviewOverrides,
    clearPreviewOverrides,
  ]);

  if (!isPreviewMode) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 1.5,
        backgroundColor: "primary.main",
        color: "primary.contrastText",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Eye size={18} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t("projectSettings.portalDisplay.previewMode")} —{" "}
          {previewTemplate?.name}
          {isDirty && " *"}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          variant="contained"
          size="small"
          onClick={saveAndPush}
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={14} /> : <Save size={14} />
          }
          sx={{
            backgroundColor: "common.white",
            color: "primary.main",
            "&:hover": { backgroundColor: "grey.100" },
          }}
        >
          {t("projectSettings.portalDisplay.saveAndPush")}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={saveDraft}
          disabled={saving || !isDirty}
          sx={{
            borderColor: "common.white",
            color: "common.white",
            "&:hover": {
              borderColor: "grey.200",
              backgroundColor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          {t("projectSettings.portalDisplay.saveDraft")}
        </Button>
        <Button
          variant="text"
          size="small"
          onClick={discard}
          disabled={saving}
          startIcon={<X size={14} />}
          sx={{
            color: "common.white",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
          }}
        >
          {t("projectSettings.portalDisplay.discard")}
        </Button>
      </Box>
    </Box>
  );
};

export default PreviewBanner;
